// @flow

import React, { Component } from "react";
import { get, isEqual, noop } from "lodash";
import type {
  PastPerformanceRace as PastPerformanceRaceType,
  RaceCardBettingInterest,
  Stat
} from "@tvg/types/Race";
import { getRunner } from "@tvg/utils/getRaceAtiveRunner";
import type { NullaryFn, UnaryFn } from "@tvg/types/Functional";
import { replaceCAPIVariables } from "@tvg/utils/capiUtils";
import PastPerformanceList from "../../_organism/PastPerformanceList";
import Toggle from "../../_atom/Toggle";
import MessageBox from "../../_molecule/MessageBox";
import Icon from "../../_static/Icons";
import PastPerformanceStatus from "../../_atom/PastPerformanceStatus";
import Spinner from "../../_static/Spinners";
import { arrowForward } from "../../_static/Icons/icons";
import buildColor from "../../_static/ColorPalette";

import {
  ListHolder,
  ListWrapper,
  MoreButton,
  LoadingState,
  EmptyStateContainer,
  MatchHolder,
  IconContainer,
  LoadingMessage,
  PastPerformanceStatusContainer,
  RunnerFlagsContainer,
  HorseOddsContainer,
  OpenPPModalButton,
  OpenPPModalArrow,
  ListContainer
} from "./styled-components";
import HorseOddsPP from "../../_molecule/HorseOddsPP";
import RunnerFlags, {
  type HandicappingRunnerFlags
} from "../../_molecule/RunnerFlags";

type Props = {
  originRace: string,
  races: PastPerformanceRaceType[],
  selectedRace: number,
  totalRaces: number,
  onRace: UnaryFn<number, void>,
  onMoreRaces: ?NullaryFn<void>,
  onMatchToggle: ?NullaryFn<void>,
  isMatchConditionsOn: boolean,
  isLoading: boolean,
  emptyMessage: string,
  paddingless: boolean,
  borderless: boolean,
  isMobile: boolean,
  activeRunner: string,
  activeRunnerBI: ?RaceCardBettingInterest,
  onRaceCard: boolean,
  postRaceReportShown: boolean,
  runnerStats: Stat[],
  seeAllPpsMessage: string,
  noRunnerCommentMessage: string,
  isSilkShown: boolean,
  isLoadingStats: boolean,
  handicappingRunnerFlags: HandicappingRunnerFlags
};

export default class InlinePastPerformance extends Component<Props> {
  static defaultProps = {
    originRace: "",
    races: [],
    selectedRace: -1,
    totalRaces: 0,
    onRace: noop,
    onMoreRaces: null,
    onMatchToggle: null,
    isMatchConditionsOn: false,
    isLoading: false,
    emptyMessage: "No replays are available for this horse.",
    paddingless: false,
    borderless: false,
    isMobile: false,
    postRaceReportShown: false,
    activeRunner: "",
    activeRunnerBI: null,
    onRaceCard: false,
    runnerStats: [],
    seeAllPpsMessage: "~runnerName~'s Info and PPs",
    noRunnerCommentMessage: "",
    isSilkShown: true,
    isLoadingStats: false,
    handicappingRunnerFlags: {
      title: "",
      subtitle: "",
      subtitleRunnerFlags: "",
      flags: []
    }
  };

  shouldComponentUpdate(nextProps: Props) {
    return (
      this.props.isMatchConditionsOn !== nextProps.isMatchConditionsOn ||
      this.props.totalRaces !== nextProps.totalRaces ||
      this.props.selectedRace !== nextProps.selectedRace ||
      this.props.races.length !== nextProps.races.length ||
      this.props.isLoading !== nextProps.isLoading ||
      this.props.isLoadingStats !== nextProps.isLoadingStats ||
      this.props.isSilkShown !== nextProps.isSilkShown ||
      !isEqual(this.props.activeRunnerBI, nextProps.activeRunnerBI) ||
      !isEqual(this.props.runnerStats, nextProps.runnerStats) ||
      !isEqual(this.props.onMoreRaces, nextProps.onMoreRaces) ||
      !isEqual(this.props.races, nextProps.races)
    );
  }

  renderLoading = () => (
    <ListHolder paddingBottom>
      <LoadingState>
        <Spinner width={21} height={21} />
        <LoadingMessage>Loading Race Replays</LoadingMessage>
      </LoadingState>
    </ListHolder>
  );

  renderEmpty = () => (
    <EmptyStateContainer key="ipp-container-messages" timeout={4000}>
      <MessageBox
        type="info"
        message={
          this.props.isMatchConditionsOn
            ? "There are no races to match."
            : this.props.emptyMessage
        }
        hasIcon
        isVertical
      />
    </EmptyStateContainer>
  );

  renderPastPerformanceStats = () => (
    <PastPerformanceStatusContainer>
      <PastPerformanceStatus
        isLoading={this.props.isLoadingStats}
        races={get(this.props.runnerStats, "starts", 0)}
        wins={get(this.props.runnerStats, "wins", 0)}
        top3={get(this.props.runnerStats, "top3", 0)}
      />
    </PastPerformanceStatusContainer>
  );

  renderRunnerFlags = () => {
    if (!this.props.activeRunnerBI) return null;

    const runner = getRunner(
      this.props.activeRunnerBI,
      this.props.activeRunner
    );
    const flagsObg = get(runner, `timeform.flags`, {});
    const hasFlags = Object.keys(flagsObg).some((flag) => flagsObg[flag]);
    const raceTypeCode = get(
      this.props.races,
      `[${this.props.selectedRace}].raceTypeCode`,
      "T"
    );

    return (
      hasFlags && (
        <RunnerFlagsContainer>
          <RunnerFlags
            runnerId={get(runner, "runnerId")}
            runnerName={this.props.activeRunner}
            raceTypeCode={raceTypeCode}
            flags={flagsObg}
            handicappingRunnerFlags={this.props.handicappingRunnerFlags}
            helpShown
          />
        </RunnerFlagsContainer>
      )
    );
  };

  renderMoreButton = (hasMoreButtonPresent: boolean = false) =>
    hasMoreButtonPresent && (
      <MoreButton
        size="big"
        type="tertiary"
        onClick={this.props.onMoreRaces}
        qaLabel="ipp-all-races-button"
      >
        {`${this.props.totalRaces} more Replays Available`}
        <IconContainer>
          <Icon icon={arrowForward} color={buildColor("blue_accent", "600")} />
        </IconContainer>
      </MoreButton>
    );

  renderOpenPPModal = () => (
    <OpenPPModalButton
      onTouchStart={noop}
      onClick={this.props.onMoreRaces}
      type="secondary_alt"
      hasRoundedCorners={false}
      isUppercase={false}
      isStretched
    >
      <span>
        {this.props.seeAllPpsMessage
          ? replaceCAPIVariables(this.props.seeAllPpsMessage, {
              runnerName: this.props.activeRunner
            })
          : "Info and PPs"}
      </span>
      <OpenPPModalArrow>
        <Icon
          size={13}
          icon={arrowForward}
          color={buildColor("blue_accent", "500")}
        />
      </OpenPPModalArrow>
    </OpenPPModalButton>
  );

  renderList = (hasMoreButtonPresent: boolean = false) => (
    <ListWrapper
      borderless={!this.props.races.length || this.props.borderless}
      data-qa-label={hasMoreButtonPresent ? "ipp-shortlist" : "ipp-fulllist"}
      isHandicappingPastPerformance
    >
      <PastPerformanceList
        isLoading={this.props.isLoading}
        races={this.props.races}
        selectedRace={this.props.selectedRace}
        onRace={this.props.onRace}
        hasMoreButtonPresent={hasMoreButtonPresent}
        isMobile={this.props.isMobile}
        postRaceReportShown={this.props.postRaceReportShown}
      />
    </ListWrapper>
  );

  renderMatchToggle = () => (
    <MatchHolder>
      <span>
        Only show races that match&nbsp;
        <strong data-qa-label="ipp-origin-race">{this.props.originRace}</strong>
        &nbsp;conditions
      </span>
      <Toggle
        on={this.props.isMatchConditionsOn}
        onClick={this.props.onMatchToggle || noop} // noop for flow
        qaLabel="ipp-match-condition-button"
      />
    </MatchHolder>
  );

  renderHorseOdds = () =>
    this.props.activeRunnerBI && (
      <HorseOddsContainer isLoading={this.props.isLoading}>
        <HorseOddsPP
          activeRunnerBI={this.props.activeRunnerBI}
          activeRunner={this.props.activeRunner}
          noRunnerCommentMessage={this.props.noRunnerCommentMessage}
          isSilkShown={this.props.isSilkShown}
        />
      </HorseOddsContainer>
    );

  render() {
    return (
      <ListContainer>
        <ListHolder
          paddingless={this.props.paddingless}
          className="expandable"
          isHandicappingPastPerformance
        >
          {this.renderPastPerformanceStats()}
          {this.renderRunnerFlags()}
          {this.renderHorseOdds()}
          {this.renderList(true)}
          {this.renderOpenPPModal()}
        </ListHolder>
      </ListContainer>
    );
  }
}
